import React, { useState, useEffect, useRef, type Ref, useImperativeHandle, useMemo, useCallback } from 'react';
import { Box, Typography, Paper, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '@core/hooks/useNotifications';
import { validateName, required } from '@core/utils/validation';
import { OperationMode, operationModeService } from '@/services';
import FormTextField from '@/components/form/FormTextField.tsx';
import { FormContainerRef } from '@/components/form/FormContainer.tsx';
import { FormContainer, WcnButton } from '@/components';
import { useSettingsStore } from '@core/store';
import { defaultOperationModes } from '@features/administration/components/operationModesSchedules/scheduleDisplay.ts';
import { LoadingOverlay } from '@/components/shared/LoadingOverlay.tsx';

export interface EditorRef {
    isDirty: boolean;
    save: () => Promise<boolean>;
}

interface OperationModeEditorProps {
    item: OperationMode;
    onSave: () => void;
    onDelete: (item: OperationMode) => void;
    onCancel: () => void;
    ref?: Ref<EditorRef>;
}

const OperationModeEditor: React.FC<OperationModeEditorProps> = ({ item, onSave, onDelete, onCancel, ref }) => {
    const { t } = useTranslation();
    const { notifyErrorResponse, notifySuccess, notifyWarning } = useNotifications();
    const [formData, setFormData] = useState<OperationMode>(item);
    const formRef = useRef<FormContainerRef>(null);
    const [formValid, setFormValid] = useState(false);
    const isEditMode = Boolean(item.id);
    const { isMobile } = useSettingsStore();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setFormData(item);
        setFormValid(true);
        formRef.current?.resetValidation();
    }, [item]);

    const isDirty = useMemo(() => {
        return formData.name !== item.name || formData.description !== item.description;
    }, [item, formData]);

    const performSave = async (): Promise<boolean> => {
        const isValid = formRef.current?.validate?.() ?? formValid;
        if (!isValid) {
            notifyWarning(formRef.current?.getRequiredWarning() || 'required');
            return false;
        }

        try {
            const isNew = !formData.id;

            const payload = isNew ? (({ id, ...rest }) => rest)(formData) : formData;

            await operationModeService.saveOperationMode(payload as OperationMode);
            notifySuccess(
                isNew
                    ? t('addedOperationMode', { mode: formData.name })
                    : t('updatedOperationMode', { mode: formData.name })
            );

            return true;
        } catch (error) {
            notifyErrorResponse(error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const handleSave = useCallback(async () => {
        const saved = await performSave();
        if (saved) onSave();
    }, [performSave]);

    const handleChange = (field: keyof OperationMode, value: any) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    useImperativeHandle(ref, () => ({ isDirty, save: performSave }), [isDirty, performSave]);

    return (
        <Paper variant="outlined" sx={{ borderRadius: '3px', overflow: 'visible' }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1
                }}
            >
                <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}
                >
                    {item.id ? t('editOperationMode') : t('addOperationMode')}
                </Typography>
            </Box>

            <Box sx={{ padding: isMobile ? '24px 10px' : '42px 100px 24px 180px' }}>
                <FormContainer formRef={formRef} value={formValid} onChange={setFormValid}>
                    <Grid container gap={isMobile ? 0 : 4}>
                        <Grid container item spacing={2}>
                            <Grid container item xs={12} spacing={4}>
                                <Grid item xs={12} sm={6}>
                                    <FormTextField
                                        name={'name'}
                                        label={t('name')}
                                        value={formData.name || ''}
                                        onChange={(e) => handleChange('name', e.target.value)}
                                        required
                                        fullWidth
                                        rules={[validateName(t)]}
                                        disabled={isEditMode}
                                        inputProps={{maxLength: 20}}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormTextField
                                        name={'description'}
                                        label={t('description')}
                                        value={formData.description || ''}
                                        onChange={(e) => handleChange('description', e.target.value)}
                                        required
                                        fullWidth
                                        rules={[required(t)]}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </FormContainer>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: isMobile ? 'center' : 'flex-end',
                        alignItems: isMobile ? 'center' : 'unset',
                        gap: 2.25,
                        mt: 7,
                        mb: 3
                    }}
                >
                    {isMobile ? (
                        <>
                            <WcnButton
                                variant="primary"
                                onClick={handleSave}
                                disabled={!formValid}
                                style={{ width: '270px' }}
                            >
                                {t('save')}
                            </WcnButton>
                            <WcnButton variant="red" onClick={onCancel} style={{ width: '270px' }}>
                                {t('cancel')}
                            </WcnButton>
                            {item.id && !defaultOperationModes.includes(item.name) && (
                                <WcnButton variant="red" onClick={() => onDelete(item)} style={{ width: '270px' }}>
                                    {t('delete')}
                                </WcnButton>
                            )}
                        </>
                    ) : (
                        <>
                            {item.id && !defaultOperationModes.includes(item.name) && (
                                <WcnButton variant="red" onClick={() => onDelete(item)}>
                                    {t('delete')}
                                </WcnButton>
                            )}
                            <WcnButton variant="red" onClick={onCancel}>
                                {t('cancel')}
                            </WcnButton>
                            <WcnButton variant="primary" onClick={handleSave} disabled={!formValid}>
                                {t('save')}
                            </WcnButton>
                        </>
                    )}
                </Box>

                <LoadingOverlay open={loading} />
            </Box>
        </Paper>
    );
};

export default OperationModeEditor;
